/**
 * IF-06 룸 WSS 메시지 v2(P2-S2) — 자체 고정 레이아웃(리틀 엔디언 · 버전 바이트). ARC §9 의 FlatBuffers 대신(FIELD-DEC-002 v0.2).
 * 스냅샷·FullState 는 기계 엔티티 · ACK 기준 델타 — ./entity.
 *
 * C→S  "in"  Input      9 B  [ver u8][machine u8][tick u32][dpad u8][buttons u8][slider i8]   (≤32 B · ARC §9.1)
 * C→S  "pi"  Ping       9 B  [ver u8][clientMs f64]
 * S→C  "po"  Pong      21 B  [ver u8][clientMs f64][serverTick u32][msIntoTick f64]
 * C→S  "ak"  Ack        5 B  [ver u8][tick u32]                         (./entity)
 * S→C  "sn"  Snapshot        엔티티 델타(./entity encodeDelta)
 * S→C  "fs"  FullState       JSON 메타 + 환경 Body12 + 기계 정수 상태(./entity · ≤64 KB · SR-NET-04)
 * S→C  "mb"  Members   JSON  { members }   · "er" Error JSON { code }
 * S→C  "ev"  Event     JSON  { t: match|result|matchEnd|queue|teamComplete|teamEnd|emote, … } — 당사자에게만 가는 것은 서버가 고른다
 * C→S  "em"  Emote     JSON  { key } — 카탈로그 키(이모트·정형 문구)만 · 멤버당 초당 1회(SR-SOC-01)
 *
 * Body12 = [index u16][x i16][y i16][z i16][quat u32] — 환경 몸체(FullState) · 위치 2 mm 단위(±65.5 m) · 회전 smallest-three(2 + 3×10 bit).
 * Ack5 = [machine u8][inputTick u32] — 이번 스냅샷 사이에 반영한 입력(입력→반영 지연 측정 · 예측 재조정 준비).
 */
/** 리모컨 상태(@picnic-field/local-host RemoteInput 과 같은 모양 — 이 패키지는 의존성을 두지 않는다) */
export interface RemoteState {
  readonly dpad: "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw" | null;
  readonly a: boolean;
  readonly b: boolean;
  readonly slider: number;
}

export const PROTOCOL_VERSION = 2;
export const MSG = {
  input: "in",
  ping: "pi",
  pong: "po",
  ack: "ak",
  snapshot: "sn",
  fullState: "fs",
  members: "mb",
  error: "er",
  /** P2-S3 S→C 모드·이모트 이벤트 JSON { t, … } · C→S 이모트 JSON { key } */
  event: "ev",
  emote: "em",
  /** P3-S2 C→S 릴레이 학생 명령 JSON { op: "ready" | "unready" | "retry" } — 자기 구간만(거절은 ev relayRejected) */
  relay: "rl",
  /**
   * 2026-09-28 원리 보기(SR-PHY-07 · 룸 화면) — C→S JSON { on: boolean, machine?: number }(학생은 제 기계 · 교사 · 칠판은 machine) ·
   * S→C JSON { tick, machine, machines: PrincipleMachine[] }(켠 연결에만 ≈10 Hz · owner = "m<번호>" · 서버 권위 월드의 읽기 전용 값)
   */
  principle: "pr",
  principleState: "pp",
  /**
   * 2026-10-11 리모컨 모드 — C→S JSON { remote: boolean }(학생 · 제 기계 · 밀어내기 · 협동 운반 · 릴레이만). 켜면 프로그램 대신 화면 리모컨이
   * 모터를 바로 · 끄면 프로그램을 처음부터. S→C ev { t: "drive", remote } · FullState me.drive("program" | "remote") · 판을 새로 실으면 밀어내기는 리모컨 · 나머지는 프로그램
   */
  drive: "dr",
} as const;

/**
 * SR-NET-05 학교망 진단(교사 · 룸 서버 공개 WSS 의 `netcheck` 룸 — 룸과 같은 매치메이크 + WS 경로 · 443 단일 포트).
 * C→S echo(바이트 ≤64 — 그대로 돌려준다) · down {kb}(S→C 16 KB 조각 blob × n 뒤 downEnd {bytes}) · up(바이트 ≤3 KB 조각) · upEnd → upResult {bytes, ms}.
 * 한 세션 상한: 내려받기 합 ≤1 MB · 올리기 합 ≤256 KB · 30초 · 초당 메시지 ≤100 — 넘으면 끊는다.
 */
export const NETCHECK_ROOM = "netcheck";
export const NETCHECK = {
  echo: "ne",
  down: "nd",
  blob: "nb",
  downEnd: "nx",
  up: "nu",
  upEnd: "nf",
  upResult: "nr",
} as const;
export const NETCHECK_LIMITS = {
  echoBytes: 64,
  blobBytes: 16 * 1024,
  downKb: 1024,
  upChunkBytes: 3 * 1024,
  upKb: 256,
  sessionMs: 30_000,
  messagesPerSecond: 100,
} as const;

/**
 * micro:bit 라디오 광장(PICNIC-MB-FIELD-001 §2 통로 ③ · §6 2단계 — 룸 서버 공개 WSS 의 `mbradio` 룸 · 코스마다 하나 · joinOrCreate {token, course}).
 * C→S send {g: 그룹 0..255, p: 패킷 바이트(1..64 · MakeCode 시뮬레이터 라디오 패킷 그대로 — 실측 36)} · S→C recv {from, name, g, p}(보낸 사람 빼고 모두에게)
 * · S→C members [{id, name, role}](들어오고 나갈 때). 받는 쪽 시뮬레이터가 그룹을 거른다(상류 RadioState) — 룸은 거르지 않는다.
 * 상한: 한 사람 초당 패킷 {@link MBRADIO_LIMITS.packetsPerSecond}(넘는 것은 조용히 버린다 — 아이의 forever 반복이 쉬지 않고 보낼 수 있다) · 방 정원 40.
 * 진짜 보드 게이트웨이(§6.7 — 교실 공중 라디오 ↔ 광장): send/recv 에 `air: true` — 공중에서 들어온 패킷(받는 화면이 📡 로 보인다).
 * 게이트웨이는 교실 여러 보드를 한 연결로 나르므로 선생님 연결은 초당 {@link MBRADIO_LIMITS.teacherPacketsPerSecond}.
 */
export const MBRADIO_ROOM = "mbradio";
export const MBRADIO = {
  send: "rs",
  recv: "rr",
  members: "rm",
} as const;
export const MBRADIO_LIMITS = {
  /** 시뮬레이터 bufferData 는 36바이트였다(2026-10-05 운영 실측) — 넉넉히 64 */
  packetBytes: 64,
  packetsPerSecond: 10,
  /** 선생님 연결(게이트웨이 보드를 꽂는 교사 화면 — 교실 여러 보드를 나른다) */
  teacherPacketsPerSecond: 40,
  maxClients: 40,
  nameChars: 24,
} as const;

export const INPUT_BYTES = 9;
export const BODY_BYTES = 12;
/** 위치 양자화 단위(m) — 2 mm */
export const POS_Q = 0.002;
const POS_MAX = 32767;
/** 스냅샷 깃발 */
export const SNAP_PAUSED = 1;

const DPAD = [null, "n", "ne", "e", "se", "s", "sw", "w", "nw"] as const;
export type Dpad = (typeof DPAD)[number];

export interface InputMsg {
  readonly machine: number;
  readonly tick: number;
  readonly remote: RemoteState;
}

export function encodeInput(m: InputMsg): Uint8Array {
  const b = new Uint8Array(INPUT_BYTES);
  const v = new DataView(b.buffer);
  v.setUint8(0, PROTOCOL_VERSION);
  v.setUint8(1, m.machine);
  v.setUint32(2, m.tick >>> 0, true);
  v.setUint8(6, Math.max(0, DPAD.indexOf(m.remote.dpad as Dpad)));
  v.setUint8(7, (m.remote.a ? 1 : 0) | (m.remote.b ? 2 : 0));
  v.setInt8(8, Math.max(-100, Math.min(100, Math.round(m.remote.slider))));
  return b;
}

/** 형식·범위가 틀리면 null(SR-NET-01 값 범위) */
export function decodeInput(bytes: Uint8Array): InputMsg | null {
  if (bytes.byteLength !== INPUT_BYTES) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (v.getUint8(0) !== PROTOCOL_VERSION) return null;
  const d = v.getUint8(6);
  const buttons = v.getUint8(7);
  const slider = v.getInt8(8);
  if (d >= DPAD.length || buttons > 3 || slider < -100 || slider > 100) return null;
  return {
    machine: v.getUint8(1),
    tick: v.getUint32(2, true),
    remote: { dpad: DPAD[d] ?? null, a: (buttons & 1) !== 0, b: (buttons & 2) !== 0, slider },
  };
}

export function encodePing(clientMs: number): Uint8Array {
  const b = new Uint8Array(9);
  const v = new DataView(b.buffer);
  v.setUint8(0, PROTOCOL_VERSION);
  v.setFloat64(1, clientMs, true);
  return b;
}
export function decodePing(bytes: Uint8Array): number | null {
  if (bytes.byteLength !== 9) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, 9);
  return v.getUint8(0) === PROTOCOL_VERSION ? v.getFloat64(1, true) : null;
}

export interface Pong {
  readonly clientMs: number;
  readonly serverTick: number;
  readonly msIntoTick: number;
}
export function encodePong(p: Pong): Uint8Array {
  const b = new Uint8Array(21);
  const v = new DataView(b.buffer);
  v.setUint8(0, PROTOCOL_VERSION);
  v.setFloat64(1, p.clientMs, true);
  v.setUint32(9, p.serverTick >>> 0, true);
  v.setFloat64(13, p.msIntoTick, true);
  return b;
}
export function decodePong(bytes: Uint8Array): Pong | null {
  if (bytes.byteLength !== 21) return null;
  const v = new DataView(bytes.buffer, bytes.byteOffset, 21);
  if (v.getUint8(0) !== PROTOCOL_VERSION) return null;
  return {
    clientMs: v.getFloat64(1, true),
    serverTick: v.getUint32(9, true),
    msIntoTick: v.getFloat64(13, true),
  };
}

// ── 몸체 자세 양자화 ──────────────────────────────────────────────────────

const qpos = (m: number) => Math.max(-POS_MAX, Math.min(POS_MAX, Math.round(m / POS_Q)));
const Q10 = 511; // 10 bit 부호 있는 성분(±1/√2 를 ±511 로)
const INV_SQRT2 = Math.SQRT1_2;

/** smallest-three — 가장 큰 성분을 빼고(부호는 양으로 맞춤) 나머지 셋을 10 bit 로 */
export function packQuat(x: number, y: number, z: number, w: number): number {
  const c = [x, y, z, w];
  let big = 0;
  for (let i = 1; i < 4; i++) if (Math.abs(c[i]!) > Math.abs(c[big]!)) big = i;
  const s = c[big]! < 0 ? -1 : 1;
  let out = big;
  for (let i = 0; i < 4; i++) {
    if (i === big) continue;
    const q = Math.max(-Q10, Math.min(Q10, Math.round(((c[i]! * s) / INV_SQRT2) * Q10)));
    out = (out << 10) | (q & 0x3ff);
  }
  return out >>> 0;
}

export function unpackQuat(p: number, out: Float32Array | number[], o: number): void {
  const big = (p >>> 30) & 3;
  const vals: number[] = [];
  for (let k = 2; k >= 0; k--) {
    let q = (p >>> (k * 10)) & 0x3ff;
    if (q & 0x200) q -= 0x400;
    vals.push((q / Q10) * INV_SQRT2);
  }
  let sum = 0;
  for (const v of vals) sum += v * v;
  const bigV = Math.sqrt(Math.max(0, 1 - sum));
  let j = 0;
  for (let i = 0; i < 4; i++) out[o + i] = i === big ? bigV : vals[j++]!;
}

/** transforms(몸체 × [x,y,z,qx,qy,qz,qw]) 의 i 번째 몸체를 Body12 로 */
export function writeBody(v: DataView, at: number, index: number, t: Float32Array): void {
  const o = index * 7;
  v.setUint16(at, index, true);
  v.setInt16(at + 2, qpos(t[o]!), true);
  v.setInt16(at + 4, qpos(t[o + 1]!), true);
  v.setInt16(at + 6, qpos(t[o + 2]!), true);
  v.setUint32(at + 8, packQuat(t[o + 3]!, t[o + 4]!, t[o + 5]!, t[o + 6]!), true);
}

/** Body12 → out(몸체 × 7) 의 그 자리에. 몸체 번호를 돌려준다 */
export function readBody(v: DataView, at: number, out: Float32Array): number {
  const index = v.getUint16(at, true);
  const o = index * 7;
  if (o + 7 > out.length) return index;
  out[o] = v.getInt16(at + 2, true) * POS_Q;
  out[o + 1] = v.getInt16(at + 4, true) * POS_Q;
  out[o + 2] = v.getInt16(at + 6, true) * POS_Q;
  unpackQuat(v.getUint32(at + 8, true), out, o + 3);
  return index;
}

// ── 공통 ─────────────────────────────────────────────────────────────

export interface Ack {
  readonly machine: number;
  readonly inputTick: number;
}

/** 스냅샷 깃발 — 열화 ④(10 Hz) 중 */
export const SNAP_HALF_RATE = 2;

export * from "./entity";
export * from "./mirror";
